import React, { useEffect, useRef, useState } from 'react';
import { BarChart3, ShieldCheck, Layers, TrendingUp, Clock } from 'lucide-react';
import { PageTitle } from '../../../_metronic/layout/core';
import { toAbsoluteUrl } from '../../../_metronic/helpers';
import { getUserData } from '../../modules/auth';

function getGreeting(date: Date) {
  const hour = date.getHours();
  if (hour < 12) return 'Bom dia';
  if (hour < 18) return 'Boa tarde';
  return 'Boa noite';
}

const features = [
  { icon: BarChart3, label: 'Relatórios' },
  { icon: Layers, label: 'Gestão' },
  { icon: ShieldCheck, label: 'Segurança' },
];

const rotatingWords = ['relatórios', 'indicadores', 'promoções', 'ferramentas', 'resultados'];

const particles = Array.from({ length: 16 }, (_, i) => i);
const bars = [38, 62, 48, 80, 56, 92];

function SplitText({ text, delay = 0, step = 0.035 }: { text: string; delay?: number; step?: number }) {
  return (
    <>
      {text.split('').map((char, i) => (
        <span
          key={i}
          className="hp-char"
          style={{ animationDelay: `${delay + i * step}s` }}
        >
          {char === ' ' ? ' ' : char}
        </span>
      ))}
    </>
  );
}

function HomePage() {
  const firstName: string | undefined = getUserData()?.name?.trim().split(' ')[0];
  const [now, setNow] = useState(new Date());
  const [wordIndex, setWordIndex] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const clock = setInterval(() => setNow(new Date()), 1000);
    const words = setInterval(() => setWordIndex((i) => (i + 1) % rotatingWords.length), 2600);
    return () => {
      clearInterval(clock);
      clearInterval(words);
    };
  }, []);

  // O efeito 3D reage ao mouse em toda a página, relativo ao centro do logo
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const page = e.currentTarget.getBoundingClientRect();
    const clamp = (v: number) => Math.max(-1, Math.min(1, v));
    const x = clamp((e.clientX - (rect.left + rect.width / 2)) / (page.width / 2));
    const y = clamp((e.clientY - (rect.top + rect.height / 2)) / (page.height / 2));
    el.style.setProperty('--rx', `${(-y * 16).toFixed(2)}deg`);
    el.style.setProperty('--ry', `${(x * 16).toFixed(2)}deg`);
  };

  const handleMouseLeave = () => {
    stageRef.current?.style.setProperty('--rx', '0deg');
    stageRef.current?.style.setProperty('--ry', '0deg');
  };

  const dateLabel = now.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
  const timeLabel = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  return (
    <div className="hp-root" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
      <PageTitle>Sistema Interno</PageTitle>
      <style>{`
        /* Remove o padding do layout só nesta página para ocupar 100% da área */
        #kt_content:has(.hp-root) { padding: 0 !important; }
        #kt_content:has(.hp-root) > div,
        #kt_content_container:has(.hp-root) {
          display: flex;
          flex-direction: column;
          flex: 1 1 auto;
          width: 100%;
          max-width: none !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .hp-root {
          --hp-blue: #2563eb;
          --hp-cyan: #06b6d4;
          --hp-pink: #f43f5e;
          --hp-violet: #7c3aed;
          --hp-green: #22c55e;
          --hp-ink: #0f172a;
          --hp-muted: #64748b;
          position: relative;
          flex: 1 1 auto;
          width: 100%;
          min-height: 600px;
          background: #f8fafc;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          isolation: isolate;
        }

        /* ---------- Background ---------- */
        .hp-grid {
          position: absolute;
          inset: -40px;
          z-index: -1;
          background-image:
            linear-gradient(rgba(37, 99, 235, .10) 1px, transparent 1px),
            linear-gradient(90deg, rgba(37, 99, 235, .10) 1px, transparent 1px);
          background-size: 40px 40px;
          -webkit-mask-image: radial-gradient(ellipse 60% 55% at 50% 50%, #000 30%, transparent 75%);
                  mask-image: radial-gradient(ellipse 60% 55% at 50% 50%, #000 30%, transparent 75%);
          animation: hp-grid-pan 8s linear infinite;
        }

        .hp-particle {
          position: absolute;
          bottom: -10px;
          border-radius: 50%;
          opacity: 0;
          z-index: -1;
          animation: hp-rise linear infinite;
        }

        /* ---------- Layout ---------- */
        .hp-content {
          width: 100%;
          max-width: 1240px;
          padding: 0 32px;
          display: grid;
          grid-template-columns: 1.1fr 1fr;
          gap: 56px;
          align-items: center;
        }

        /* ---------- Left column ---------- */
        .hp-topline {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 10px;
        }
        .hp-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 14px 6px 10px;
          border-radius: 999px;
          background: #fff;
          border: 1px solid rgba(37, 99, 235, .15);
          box-shadow: 0 4px 16px rgba(15, 23, 42, .06);
          font-size: 13px;
          font-weight: 600;
          color: var(--hp-blue);
        }
        .hp-badge--clock { color: var(--hp-muted); border-color: rgba(15, 23, 42, .08); }
        .hp-badge--clock strong { color: var(--hp-ink); font-variant-numeric: tabular-nums; }
        .hp-badge-dot {
          position: relative;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--hp-green);
        }
        .hp-badge-dot::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 50%;
          background: var(--hp-green);
          animation: hp-ping 1.8s cubic-bezier(0, 0, .2, 1) infinite;
        }

        .hp-greeting {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 28px 0 8px;
          font-size: 20px;
          font-weight: 600;
          color: var(--hp-muted);
        }
        .hp-greeting strong { color: var(--hp-ink); }
        .hp-wave {
          display: inline-block;
          transform-origin: 70% 70%;
          animation: hp-wave 2.4s ease-in-out 1s infinite;
        }

        .hp-title {
          margin: 0;
          font-size: clamp(36px, 4.4vw, 60px);
          line-height: 1.08;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: var(--hp-ink);
        }
        .hp-title-line { display: block; padding-bottom: .08em; }
        .hp-char {
          display: inline-block;
          animation: hp-char-in .7s cubic-bezier(.2, .8, .2, 1) both;
        }

        .hp-gradient-text {
          display: inline-block;
          background: linear-gradient(90deg, var(--hp-blue), var(--hp-cyan), var(--hp-violet), var(--hp-blue));
          background-size: 300% 100%;
          -webkit-background-clip: text;
                  background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: hp-char-in 1s cubic-bezier(.2, .8, .2, 1) .9s both, hp-gradient 6s ease-in-out infinite;
        }

        .hp-underline {
          position: relative;
          display: block;
          height: 5px;
          width: 140px;
          margin-top: 16px;
          border-radius: 99px;
          background: linear-gradient(90deg, var(--hp-pink), var(--hp-cyan));
          transform-origin: left;
          overflow: hidden;
          animation: hp-scale-x .9s cubic-bezier(.2, .8, .2, 1) 1.1s both;
        }
        .hp-underline::after {
          content: '';
          position: absolute;
          inset: 0;
          width: 30%;
          background: rgba(255, 255, 255, .8);
          animation: hp-underline-run 2.4s ease-in-out 2s infinite;
        }

        .hp-subtitle {
          margin: 24px 0 0;
          max-width: 540px;
          font-size: 17px;
          line-height: 1.7;
          color: var(--hp-muted);
        }

        .hp-rotator {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 14px;
          font-size: 18px;
          font-weight: 600;
          color: var(--hp-ink);
        }
        .hp-rotator-window {
          position: relative;
          display: inline-block;
          height: 1.5em;
          min-width: 150px;
          overflow: hidden;
          vertical-align: bottom;
        }
        .hp-rotator-word {
          position: absolute;
          left: 0;
          top: 0;
          color: var(--hp-blue);
          white-space: nowrap;
          animation: hp-word-in .55s cubic-bezier(.2, .8, .2, 1) both;
        }
        .hp-caret {
          display: inline-block;
          width: 2px;
          height: 1.1em;
          background: var(--hp-blue);
          animation: hp-blink 1s steps(1) infinite;
        }

        .hp-features {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 32px;
        }
        .hp-chip {
          position: relative;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 16px;
          border-radius: 12px;
          background: #fff;
          border: 1px solid rgba(15, 23, 42, .06);
          box-shadow: 0 6px 20px rgba(15, 23, 42, .05);
          font-size: 14px;
          font-weight: 600;
          color: var(--hp-ink);
          overflow: hidden;
          transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease;
        }
        .hp-chip::after {
          content: '';
          position: absolute;
          top: 0;
          left: -80%;
          width: 50%;
          height: 100%;
          background: linear-gradient(100deg, transparent, rgba(37, 99, 235, .12), transparent);
          transform: skewX(-20deg);
          transition: left .6s ease;
        }
        .hp-chip:hover {
          transform: translateY(-4px);
          border-color: rgba(37, 99, 235, .3);
          box-shadow: 0 14px 30px rgba(37, 99, 235, .18);
        }
        .hp-chip:hover::after { left: 130%; }
        .hp-chip:hover .hp-chip-icon { transform: rotate(-8deg) scale(1.1); }
        .hp-chip-icon {
          display: grid;
          place-items: center;
          width: 28px;
          height: 28px;
          border-radius: 8px;
          color: #fff;
          background: linear-gradient(135deg, var(--hp-blue), var(--hp-cyan));
          transition: transform .3s ease;
        }

        /* ---------- Right column (logo stage) ---------- */
        .hp-stage {
          --rx: 0deg;
          --ry: 0deg;
          position: relative;
          display: grid;
          place-items: center;
          aspect-ratio: 1 / 1;
          max-width: 500px;
          width: 100%;
          margin: 0 auto;
          perspective: 1000px;
        }
        .hp-tilt {
          position: absolute;
          inset: 0;
          display: grid;
          place-items: center;
          transform-style: preserve-3d;
          transform: rotateX(var(--rx)) rotateY(var(--ry));
          transition: transform .6s cubic-bezier(.2, .8, .2, 1);
        }

        .hp-ring {
          position: absolute;
          border-radius: 50%;
          border: 2px dashed rgba(37, 99, 235, .35);
        }
        .hp-ring--1 { inset: 4%;  animation: hp-spin 18s linear infinite; }
        .hp-ring--2 { inset: 15%; border-style: solid; border-color: rgba(6, 182, 212, .35); animation: hp-spin 12s linear infinite reverse; }
        .hp-ring--3 { inset: 26%; border-color: rgba(244, 63, 94, .3); animation: hp-spin 9s linear infinite; }

        .hp-orbit-dot {
          position: absolute;
          top: -6px;
          left: 50%;
          width: 12px;
          height: 12px;
          margin-left: -6px;
          border-radius: 50%;
          box-shadow: 0 0 0 4px rgba(255, 255, 255, .9), 0 0 18px currentColor;
        }
        .hp-ring--1 .hp-orbit-dot { background: var(--hp-blue); color: var(--hp-blue); }
        .hp-ring--2 .hp-orbit-dot { background: var(--hp-cyan); color: var(--hp-cyan); top: auto; bottom: -6px; }
        .hp-ring--3 .hp-orbit-dot { background: var(--hp-pink); color: var(--hp-pink); }

        .hp-pulse {
          position: absolute;
          inset: 30%;
          border-radius: 50%;
          border: 2px solid rgba(37, 99, 235, .35);
          animation: hp-pulse-out 4s cubic-bezier(.2, .6, .3, 1) infinite;
        }
        .hp-pulse:nth-of-type(2) { animation-delay: 1.33s; }
        .hp-pulse:nth-of-type(3) { animation-delay: 2.66s; }

        .hp-card {
          position: relative;
          z-index: 2;
          width: 74%;
          padding: 28px 32px;
          border-radius: 24px;
          background: #fff;
          box-shadow:
            0 30px 60px -20px rgba(15, 23, 42, .25),
            0 0 0 1px rgba(15, 23, 42, .04);
          overflow: hidden;
          transform: translateZ(60px);
          animation: hp-float 6s ease-in-out infinite;
        }
        .hp-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -60%;
          width: 40%;
          height: 100%;
          background: linear-gradient(100deg, transparent, rgba(255, 255, 255, .85), transparent);
          transform: skewX(-20deg);
          animation: hp-shine 3.5s ease-in-out 1.6s infinite;
          z-index: 1;
        }
        .hp-card::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 2px;
          background: conic-gradient(from var(--hp-angle, 0deg), transparent 0 70%, var(--hp-blue), var(--hp-cyan), transparent 100%);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          animation: hp-border-spin 4s linear infinite;
        }
        .hp-card img {
          position: relative;
          display: block;
          width: 100%;
          height: auto;
        }

        /* ---------- Floating widgets ---------- */
        .hp-widget {
          position: absolute;
          z-index: 3;
          padding: 12px 14px;
          border-radius: 14px;
          background: #fff;
          border: 1px solid rgba(15, 23, 42, .05);
          box-shadow: 0 16px 36px rgba(15, 23, 42, .14);
          font-size: 12px;
          font-weight: 600;
          color: var(--hp-muted);
        }
        .hp-widget-title {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 8px;
          color: var(--hp-ink);
          white-space: nowrap;
        }
        .hp-widget--bars  { top: 6%;  right: -2%; transform: translateZ(90px); animation: hp-pop .7s cubic-bezier(.34, 1.56, .64, 1) 1.2s both, hp-float 7s ease-in-out 2s infinite; }
        .hp-widget--line  { bottom: 8%; left: -4%; transform: translateZ(90px); animation: hp-pop .7s cubic-bezier(.34, 1.56, .64, 1) 1.45s both, hp-float 8s ease-in-out 2.2s infinite reverse; }
        .hp-widget--ring  { bottom: 2%; right: 4%; transform: translateZ(70px); animation: hp-pop .7s cubic-bezier(.34, 1.56, .64, 1) 1.7s both, hp-float 6.5s ease-in-out 2.4s infinite; }
        .hp-widget--bars .hp-widget-title svg { color: var(--hp-blue); }
        .hp-widget--line .hp-widget-title svg { color: var(--hp-green); }

        .hp-bars {
          display: flex;
          align-items: flex-end;
          gap: 5px;
          height: 46px;
        }
        .hp-bar {
          width: 9px;
          border-radius: 4px 4px 2px 2px;
          background: linear-gradient(180deg, var(--hp-cyan), var(--hp-blue));
          transform-origin: bottom;
          animation: hp-bar-grow 2.8s cubic-bezier(.2, .8, .2, 1) infinite;
        }

        .hp-spark path {
          fill: none;
          stroke: var(--hp-green);
          stroke-width: 2.5;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-dasharray: 160;
          animation: hp-draw 3.2s ease-in-out 1.6s infinite;
        }
        .hp-spark circle {
          fill: var(--hp-green);
          animation: hp-dot-pop 3.2s ease-in-out 1.6s infinite;
        }

        .hp-ring-progress {
          display: flex;
          align-items: center;
          gap: 10px;
        }
        .hp-ring-progress svg { transform: rotate(-90deg); }
        .hp-ring-progress .hp-track { fill: none; stroke: #e2e8f0; stroke-width: 5; }
        .hp-ring-progress .hp-value {
          fill: none;
          stroke: var(--hp-pink);
          stroke-width: 5;
          stroke-linecap: round;
          stroke-dasharray: 100.5;
          animation: hp-progress 3.6s cubic-bezier(.2, .8, .2, 1) 1.8s infinite;
        }
        .hp-ring-progress strong { display: block; color: var(--hp-ink); font-size: 15px; }

        /* ---------- Entrance helpers ---------- */
        .hp-in      { animation: hp-fade-up .8s cubic-bezier(.2, .8, .2, 1) both; }
        .hp-zoom-in { animation: hp-zoom-in 1.1s cubic-bezier(.2, .8, .2, 1) .2s both; }

        /* ---------- Keyframes ---------- */
        @property --hp-angle { syntax: '<angle>'; initial-value: 0deg; inherits: false; }

        @keyframes hp-fade-up  { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: none; } }
        @keyframes hp-char-in  { from { opacity: 0; transform: translateY(.6em) rotateX(-80deg); filter: blur(6px); } to { opacity: 1; transform: none; filter: none; } }
        @keyframes hp-zoom-in  { from { opacity: 0; transform: scale(.85) rotate(-6deg); } to { opacity: 1; transform: none; } }
        @keyframes hp-pop      { from { opacity: 0; scale: .6; } to { opacity: 1; scale: 1; } }
        @keyframes hp-scale-x  { from { transform: scaleX(0); } to { transform: scaleX(1); } }
        @keyframes hp-underline-run { 0% { left: -30%; } 60%, 100% { left: 110%; } }
        @keyframes hp-gradient { 0%, 100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
        @keyframes hp-spin     { to { transform: rotate(360deg); } }
        @keyframes hp-float    { 0%, 100% { translate: 0 0; } 50% { translate: 0 -16px; } }
        @keyframes hp-shine    { 0% { left: -60%; } 40%, 100% { left: 130%; } }
        @keyframes hp-ping     { 75%, 100% { transform: scale(2.4); opacity: 0; } }
        @keyframes hp-wave     { 0%, 60%, 100% { transform: rotate(0); } 10%, 30% { transform: rotate(16deg); } 20%, 40% { transform: rotate(-8deg); } }
        @keyframes hp-blink    { 50% { opacity: 0; } }
        @keyframes hp-word-in  { from { opacity: 0; transform: translateY(100%); filter: blur(4px); } to { opacity: 1; transform: none; filter: none; } }
        @keyframes hp-grid-pan { from { transform: translate(0, 0); } to { transform: translate(40px, 40px); } }
        @keyframes hp-pulse-out { from { transform: scale(1); opacity: .8; } to { transform: scale(2.1); opacity: 0; } }
        @keyframes hp-border-spin { to { --hp-angle: 360deg; } }
        @keyframes hp-bar-grow { 0% { transform: scaleY(.15); } 35%, 75% { transform: scaleY(1); } 100% { transform: scaleY(.15); } }
        @keyframes hp-draw     { 0% { stroke-dashoffset: 160; } 45%, 80% { stroke-dashoffset: 0; } 100% { stroke-dashoffset: -160; } }
        @keyframes hp-dot-pop  { 0%, 35% { r: 0; } 45%, 80% { r: 3.5; } 100% { r: 0; } }
        @keyframes hp-progress { 0% { stroke-dashoffset: 100.5; } 45%, 80% { stroke-dashoffset: 6; } 100% { stroke-dashoffset: 100.5; } }
        @keyframes hp-rise {
          0%   { transform: translateY(0) scale(.6); opacity: 0; }
          10%  { opacity: .8; }
          90%  { opacity: .6; }
          100% { transform: translateY(-85vh) scale(1); opacity: 0; }
        }

        /* ---------- Responsive ---------- */
        @media (max-width: 991px) {
          .hp-root { padding: 48px 0; }
          .hp-content { grid-template-columns: 1fr; gap: 40px; text-align: center; }
          .hp-topline, .hp-greeting, .hp-rotator, .hp-features { justify-content: center; }
          .hp-subtitle, .hp-underline { margin-left: auto; margin-right: auto; }
          .hp-stage { max-width: 380px; }
        }
      `}</style>

      {/* Background */}
      <div className="hp-grid" />
      {particles.map((i) => (
        <span
          key={i}
          className="hp-particle"
          style={{
            left: `${(i * 37) % 100}%`,
            width: 4 + (i % 4) * 2,
            height: 4 + (i % 4) * 2,
            background: ['#2563eb', '#06b6d4', '#7c3aed', '#f43f5e'][i % 4],
            animationDuration: `${12 + (i % 5) * 3}s`,
            animationDelay: `${(i * 1.3) % 12}s`,
          }}
        />
      ))}

      <main className="hp-content">
        {/* Left */}
        <div>
          <div className="hp-topline">
            <span className="hp-badge hp-in" style={{ animationDelay: '.05s' }}>
              <span className="hp-badge-dot" />
              Portal administrativo
            </span>
            <span className="hp-badge hp-badge--clock hp-in" style={{ animationDelay: '.15s' }}>
              <Clock size={14} />
              <span style={{ textTransform: 'capitalize' }}>{dateLabel}</span> · <strong>{timeLabel}</strong>
            </span>
          </div>

          <p className="hp-greeting hp-in" style={{ animationDelay: '.25s' }}>
            {getGreeting(now)}{firstName && <>, <strong>{firstName}</strong></>}
            <span className="hp-wave">👋</span>
          </p>

          <h1 className="hp-title">
            <span className="hp-title-line">
              <SplitText text="Seja bem-vindo ao" delay={0.35} />
            </span>
            <span className="hp-title-line">
              <span className="hp-gradient-text">Sistema Interno</span>
            </span>
          </h1>
          <span className="hp-underline" />

          <p className="hp-subtitle hp-in" style={{ animationDelay: '1.2s' }}>
            Seu portal centralizado para gestão administrativa. Acesse ferramentas,
            relatórios e recursos de forma segura e eficiente.
          </p>

          <div className="hp-rotator hp-in" style={{ animationDelay: '1.35s' }}>
            Tudo sobre seus
            <span className="hp-rotator-window">
              <span key={wordIndex} className="hp-rotator-word">{rotatingWords[wordIndex]}</span>
            </span>
            <span className="hp-caret" />
          </div>

          <div className="hp-features">
            {features.map(({ icon: Icon, label }, idx) => (
              <span key={label} className="hp-chip hp-in" style={{ animationDelay: `${1.5 + idx * 0.12}s` }}>
                <span className="hp-chip-icon"><Icon size={16} /></span>
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Right */}
        <div
          ref={stageRef}
          className="hp-stage hp-zoom-in"
        >
          <div className="hp-tilt">
            <span className="hp-pulse" />
            <span className="hp-pulse" />
            <span className="hp-pulse" />
            <div className="hp-ring hp-ring--1"><span className="hp-orbit-dot" /></div>
            <div className="hp-ring hp-ring--2"><span className="hp-orbit-dot" /></div>
            <div className="hp-ring hp-ring--3"><span className="hp-orbit-dot" /></div>

            <div className="hp-card">
              <img src={toAbsoluteUrl('/media/logos/logo-wide.png')} alt="Logo do Sistema Interno" />
            </div>

            <div className="hp-widget hp-widget--bars">
              <div className="hp-widget-title"><BarChart3 size={14} /> Relatórios</div>
              <div className="hp-bars">
                {bars.map((h, i) => (
                  <span key={i} className="hp-bar" style={{ height: `${h}%`, animationDelay: `${1.4 + i * 0.12}s` }} />
                ))}
              </div>
            </div>

            <div className="hp-widget hp-widget--line">
              <div className="hp-widget-title"><TrendingUp size={14} /> Desempenho</div>
              <svg className="hp-spark" width="120" height="40" viewBox="0 0 120 40">
                <path d="M2 34 L22 26 L40 30 L58 16 L76 20 L96 8 L116 4" />
                <circle cx="116" cy="4" r="0" />
              </svg>
            </div>

            <div className="hp-widget hp-widget--ring">
              <div className="hp-ring-progress">
                <svg width="38" height="38" viewBox="0 0 38 38">
                  <circle className="hp-track" cx="19" cy="19" r="16" />
                  <circle className="hp-value" cx="19" cy="19" r="16" />
                </svg>
                <div>
                  <strong>Seguro</strong>
                  Acesso protegido
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default HomePage;
